import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  TrendingUp,
  Info,
} from "lucide-react";

const MaxSubarrayVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Standard Case",
      array: [-2, 1, -3, 4, -1, 2, 1, -5, 4],
    },
    { id: 2, name: "Single Element", array: [1] },
    {
      id: 3,
      name: "All Negatives",
      array: [-2, -1, -3],
    },
    {
      id: 4,
      name: "All Positives",
      array: [5, 4, -1, 7, 8],
    },
    {
      id: 5,
      name: "Start with Positive",
      array: [1, 2, -1, -2, 2, 1, -2, 1],
    },
    {
      id: 6,
      name: "Contains Zero",
      array: [-2, 1, -3, 4, 0, 1, -5, 4],
    },
  ];

  // State setup
  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [array, setArray] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code for Kadane's Algorithm
  const pseudoCode = [
    { line: "function maxSubArray(nums):", indent: 0 },
    { line: "globalMax = nums[0]", indent: 1 },
    { line: "currentMax = nums[0]", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 1 to n-1:", indent: 1 },
    { line: "num = nums[i]", indent: 2 },
    { line: "// Start new subarray or extend current one", indent: 2 },
    { line: "currentMax = max(num, currentMax + num)", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Update global max if needed", indent: 2 },
    { line: "globalMax = max(globalMax, currentMax)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return globalMax", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums) => {
    const n = nums.length;
    if (n === 0)
      return [
        {
          step: 0,
          description: "Array is empty.",
          isFinal: true,
          globalMaxSum: 0,
          currentSum: 0,
          maxSubarrayRange: [-1, -1],
          currentSubarrayStart: -1,
        },
      ];

    const allSteps = [];
    let stepCount = 0;

    let globalMax = nums[0];
    let currentMax = nums[0];
    let start = 0;
    let end = 0;
    let currentStart = 0;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        globalMaxSum: globalMax,
        currentSum: currentMax,
        maxSubarrayRange: [start, end],
        currentSubarrayStart: currentStart,
        ...details,
      });
    };

    addStep(
      `Initialize globalMax and currentMax with nums[0] (${nums[0]}).`,
      1,
      { currentIndex: 0 }
    );

    for (let i = 1; i < n; i++) {
      const num = nums[i];

      addStep(`Processing index ${i} (value ${num}).`, 4, { currentIndex: i });

      addStep(
        `Compare starting new (${num}) vs extending (${currentMax + num}).`,
        7,
        { currentIndex: i }
      );
      if (num > currentMax + num) {
        currentMax = num;
        currentStart = i;
        addStep(
          `Starting new subarray is better. currentMax = ${currentMax}.`,
          7,
          { currentIndex: i }
        );
      } else {
        currentMax = currentMax + num;
        addStep(
          `Extending subarray is better. currentMax = ${currentMax}.`,
          7,
          { currentIndex: i }
        );
      }

      addStep(
        `Compare currentMax (${currentMax}) with globalMax (${globalMax}).`,
        10,
        { currentIndex: i }
      );
      if (currentMax > globalMax) {
        globalMax = currentMax;
        start = currentStart;
        end = i;
        addStep(`New globalMax found: ${globalMax}.`, 10, { currentIndex: i });
      }
    }

    addStep(`Finished. Maximum sum is ${globalMax}.`, 12, {
      isFinal: true,
      currentIndex: n - 1,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN) || nums.length === 0) {
        throw new Error("Input must be a non-empty array of numbers.");
      }
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="text-blue-500" /> Maximum Subarray
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums`, find the contiguous subarray
                (containing at least one number) which has the largest sum and
                return its sum.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: -2,1,-3,4,-1,2,1,-5,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative">
              <div className="flex flex-wrap gap-2">
                {array.map((num, idx) => {
                  const [maxStart, maxEnd] =
                    currentStepData.maxSubarrayRange || [-1, -1];
                  const [currentStart, currentEnd] = [
                    currentStepData.currentSubarrayStart,
                    currentStepData.currentIndex,
                  ];

                  const isMax = idx >= maxStart && idx <= maxEnd;
                  const isCurrent = idx >= currentStart && idx <= currentEnd;
                  const isProcessing = idx === currentStepData.currentIndex;

                  return (
                    <div key={idx} className="text-center">
                      <div
                        className={`w-14 h-14 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300
                        ${
                          isMax
                            ? "bg-green-200 border-green-400"
                            : isCurrent
                            ? "bg-yellow-100 border-yellow-300"
                            : "bg-gray-100 border-gray-300"
                        }
                        ${
                          isProcessing ? "scale-110 ring-2 ring-blue-500" : ""
                        }`}
                      >
                        {num}
                      </div>
                      <span className="text-xs text-gray-500 mt-1">{idx}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-center">
              <div className="p-2 rounded bg-yellow-100">
                <div className="text-xs text-yellow-800 font-medium">
                  Current Max Sum
                </div>
                <div className="font-bold text-lg text-yellow-900">
                  {currentStepData.currentSum ?? "-"}
                </div>
              </div>
              <div className="p-2 rounded bg-green-100">
                <div className="text-xs text-green-800 font-medium">
                  Global Max Sum
                </div>
                <div className="font-bold text-lg text-green-900">
                  {currentStepData.globalMaxSum ?? "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Kadane's Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              Kadane's algorithm is a dynamic programming approach that solves
              this problem in linear time. It maintains two variables:
              `currentMax` (the maximum sum of a subarray ending at the current
              position) and `globalMax` (the maximum sum found so far). As it
              iterates through the array, it decides for each element whether
              it's better to extend the current subarray or start a new one.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A key realization is that if `currentMax` becomes negative,
                  it's always better to start a new subarray from the next
                  element.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The algorithm elegantly handles arrays with all negative
                  numbers by correctly finding the least negative number.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  It solves the problem in a single pass, making it highly
                  efficient.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaxSubarrayVisualizer;
